/**
 * How a Desktop container is viewed — the icon size or list, and the sort —
 * and what that means for drawing it. Pure: no React, no network.
 *
 * A folder WINDOW lays its contents out by its view: sorted, flowing to fill
 * the window, reflowing as the window is resized (the way a folder window
 * behaves in Windows Explorer or Finder). The DESKTOP itself keeps icons
 * where the person put them; its view sets only the icon size, and "Sort by"
 * there re-arranges once.
 *
 * Views are style-neutral (see `DesktopLayoutViewMode` in @fias/db-types):
 * each desktop style offers its own set and shows a mode it lacks as its
 * nearest equivalent, so switching style never loses a folder's setting.
 */
import type { FolderView, SortDirection, SortKey, ViewMode } from './desktop-layout';

export type DesktopStyle = 'windows' | 'mac';

export const DEFAULT_FOLDER_VIEW: FolderView = { mode: 'icons-md', sort: 'name', dir: 'asc' };
export const DEFAULT_TRASH_VIEW: FolderView = { mode: 'details', sort: 'modified', dir: 'desc' };

export interface ModeOption {
  mode: ViewMode;
  label: string;
}

/** The view menu, per style, in menu order. */
export const STYLE_MODES: Record<DesktopStyle, readonly ModeOption[]> = {
  windows: [
    { mode: 'icons-xl', label: 'Extra large icons' },
    { mode: 'icons-lg', label: 'Large icons' },
    { mode: 'icons-md', label: 'Medium icons' },
    { mode: 'icons-sm', label: 'Small icons' },
    { mode: 'list', label: 'List' },
    { mode: 'details', label: 'Details' },
  ],
  mac: [
    { mode: 'icons-md', label: 'as Icons' },
    { mode: 'details', label: 'as List' },
    { mode: 'columns', label: 'as Columns' },
    { mode: 'gallery', label: 'as Gallery' },
  ],
};

/** The icon sizes, smallest first — the Mac icon-size slider's stops. */
export const ICON_MODES: readonly ViewMode[] = ['icons-sm', 'icons-md', 'icons-lg', 'icons-xl'];

export function isIconMode(mode: ViewMode): boolean {
  return mode.startsWith('icons-');
}

/** The mode a style actually draws for a stored one. */
export function effectiveMode(mode: ViewMode, style: DesktopStyle): ViewMode {
  if (style === 'windows') {
    if (mode === 'columns') return 'details';
    if (mode === 'gallery') return 'icons-xl';
    return mode;
  }
  // Finder has no Windows-style List (small icons flowing in columns); its
  // List is the Details table.
  if (mode === 'list') return 'details';
  return mode;
}

/** Which entry of the style's view menu is current. */
export function menuModeFor(mode: ViewMode, style: DesktopStyle): ViewMode {
  const drawn = effectiveMode(mode, style);
  if (style === 'mac' && isIconMode(drawn)) return 'icons-md';
  return drawn;
}

export const SORT_LABELS: Record<SortKey, string> = {
  name: 'Name',
  modified: 'Date modified',
  type: 'Type',
  size: 'Size',
};

/** The direction a sort starts in when first chosen: names A→Z, types by
 *  name, the newest and the largest first. */
export function firstDirection(sort: SortKey): SortDirection {
  return sort === 'modified' || sort === 'size' ? 'desc' : 'asc';
}

/** Choosing a sort: the same key flips direction; a new key starts fresh. */
export function chooseSort(view: FolderView, sort: SortKey): FolderView {
  if (view.sort === sort) return { ...view, dir: view.dir === 'asc' ? 'desc' : 'asc' };
  return { ...view, sort, dir: firstDirection(sort) };
}

/** The server listing order for a view's files (they are paged, so the server
 *  sorts them; folders are all loaded and sorted here). */
export function listSortFor(view: FolderView): {
  sort: 'name' | 'updated' | 'kind' | 'size';
  direction: SortDirection;
} {
  const sort = { name: 'name', modified: 'updated', type: 'kind', size: 'size' } as const;
  return { sort: sort[view.sort], direction: view.dir };
}

const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/**
 * Folders' order in a view. Folders always come before files (both desktop
 * styles do this by default); among themselves they sort by name — or by date
 * when sorting by date. They have no size or type, so those sort them by name.
 */
export function compareFoldersForView(
  a: { name: string; updatedAt?: string | null; key: string },
  b: { name: string; updatedAt?: string | null; key: string },
  view: FolderView,
): number {
  let order = 0;
  if (view.sort === 'modified') {
    order = (a.updatedAt ?? '').localeCompare(b.updatedAt ?? '');
  }
  if (order === 0) order = byName(a.name, b.name);
  if (order === 0) order = a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  const flip = view.sort === 'modified' || view.sort === 'name' ? view.dir === 'desc' : false;
  return flip ? -order : order;
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

/**
 * How a view is drawn:
 *   - `grid`    icons in rows, left to right (the icon sizes);
 *   - `flow`    small icons in columns, top to bottom (Windows' List);
 *   - `rows`    one item per row with columns (Details / Finder's List).
 * Columns and Gallery have their own components and don't use cells.
 */
export type ViewLayout = 'grid' | 'flow' | 'rows';

export interface ViewMetrics {
  layout: ViewLayout;
  cellW: number;
  cellH: number;
  pad: number;
  /** Icon (or thumbnail) edge, in px. */
  icon: number;
  /** Where the name goes. */
  label: 'below' | 'right';
  /** Images show their picture, not a generic icon. */
  thumbnails: boolean;
}

const GRID: Record<'icons-xl' | 'icons-lg' | 'icons-md' | 'icons-sm', ViewMetrics> = {
  'icons-xl': {
    layout: 'grid',
    cellW: 196,
    cellH: 212,
    pad: 12,
    icon: 160,
    label: 'below',
    thumbnails: true,
  },
  'icons-lg': {
    layout: 'grid',
    cellW: 136,
    cellH: 146,
    pad: 12,
    icon: 96,
    label: 'below',
    thumbnails: true,
  },
  'icons-md': {
    layout: 'grid',
    cellW: 104,
    cellH: 100,
    pad: 12,
    icon: 48,
    label: 'below',
    thumbnails: true,
  },
  'icons-sm': {
    layout: 'grid',
    cellW: 200,
    cellH: 28,
    pad: 8,
    icon: 16,
    label: 'right',
    thumbnails: false,
  },
};

/** Finder draws small icons with the name below, like the larger sizes. */
const MAC_SMALL: ViewMetrics = {
  layout: 'grid',
  cellW: 88,
  cellH: 72,
  pad: 12,
  icon: 32,
  label: 'below',
  thumbnails: true,
};

const FLOW: ViewMetrics = {
  layout: 'flow',
  cellW: 220,
  cellH: 26,
  pad: 8,
  icon: 16,
  label: 'right',
  thumbnails: false,
};

const ROWS: ViewMetrics = {
  layout: 'rows',
  cellW: 0,
  cellH: 28,
  pad: 0,
  icon: 16,
  label: 'right',
  thumbnails: false,
};

/** Metrics for a mode as a style draws it (columns and gallery fall back to
 *  rows / large icons for the parts that still need cells). */
export function metricsFor(mode: ViewMode, style: DesktopStyle): ViewMetrics {
  const drawn = effectiveMode(mode, style);
  if (drawn === 'icons-sm' && style === 'mac') return MAC_SMALL;
  if (drawn === 'list') return FLOW;
  if (drawn === 'details' || drawn === 'columns') return ROWS;
  if (drawn === 'gallery') return GRID['icons-lg'];
  return GRID[drawn];
}

/** Height of the Details header row, which sits above the first row. */
export const DETAILS_HEADER_H = 30;
